// Cola de recetas pendientes (2026-09-11, buscador 2026-09-11) — pantalla de
// inicio de la app de Farmacia Interna. Lectura DIRECTA a Medplum (mismo
// patrón que useFarmaciaInternaHabilitada.ts/SelectorRecurso.tsx: la
// AccessPolicy de Director Técnico Q.F./B.F. ya permite leer
// MedicationRequest/Patient sin restricción de compartimento) — de TODO el
// consultorio, no de un paciente puntual. Esto es lo que resuelve "enviar
// automáticamente la receta a farmacia": no hace falta ningún envío ni cola
// aparte, es una vista de lo que el médico ya prescribió (status=active).
//
// Filtrado de ya-dispensadas: NO se puede marcar el MedicationRequest como
// completed — rechazado en vivo por el propio sistema ("es inmutable, usa
// una adenda (Provenance)", ver FormularioDispensacion.tsx) — así que se
// excluye por la existencia de un MedicationDispense que lo referencie,
// mismo criterio que el chequeo "yaDispensada" del formulario.
//
// Por defecto solo hoy, buscador para lo viejo: la lista completa (hasta
// _count) YA trae recetas de cualquier fecha — "hoy" es solo un filtro de
// render, no una limitación de la consulta. Con texto en el buscador se
// ignora ese filtro de fecha y se amplía el _count para cubrir más
// histórico; sin texto, vuelve a mostrar solo las de hoy.
import { useResource, useSearchResources } from '@medplum/react-hooks';
import { formatHumanName } from '@medplum/core';
import type { MedicationDispense, MedicationRequest, Patient } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Link } from 'react-router';

function yaDispensada(receta: MedicationRequest, dispensaciones: MedicationDispense[]): boolean {
  return dispensaciones.some((md) => md.authorizingPrescription?.some((ref) => ref.reference === `MedicationRequest/${receta.id}`));
}

function esDeHoy(authoredOn: string | undefined): boolean {
  if (!authoredOn) return false;
  const fecha = new Date(authoredOn);
  const hoy = new Date();
  return fecha.getFullYear() === hoy.getFullYear() && fecha.getMonth() === hoy.getMonth() && fecha.getDate() === hoy.getDate();
}

/** Insensible a tildes ("losartan" debe encontrar "Losartán") — \p{Diacritic} evita escribir el carácter combinante literal en el código fuente. */
function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

export function ColaRecetas(): JSX.Element {
  const { t } = useTranslation();
  const [busqueda, setBusqueda] = useState('');
  const buscando = busqueda.trim().length > 0;

  const [recetas] = useSearchResources('MedicationRequest', { status: 'active', _sort: '-authoredon', _count: buscando ? 200 : 50 });
  // Ventana razonable: alcanza para cubrir las dispensaciones de las recetas
  // activas/recientes sin traer el historial completo del consultorio en
  // cada carga de la cola.
  const [dispensaciones] = useSearchResources('MedicationDispense', { _count: 200, _sort: '-_lastUpdated' });

  const idsPacientes = [...new Set((recetas ?? []).map((r) => r.subject?.reference?.split('/')[1]).filter((id): id is string => !!id))];
  const [pacientes] = useSearchResources('Patient', idsPacientes.length > 0 ? { _id: idsPacientes.join(','), _count: idsPacientes.length } : undefined);
  const nombrePorPaciente = new Map((pacientes ?? []).map((p) => [p.id!, formatHumanName(p.name?.[0])]));

  if (recetas === undefined || dispensaciones === undefined) {
    return <p className="text-sm text-hce-muted">{t('farmaciaApp.cargando')}</p>;
  }

  const textoBuscado = normalizar(busqueda);
  const pendientes = recetas.filter((mr) => {
    if (yaDispensada(mr, dispensaciones)) return false;
    if (buscando) {
      const patientId = mr.subject?.reference?.split('/')[1];
      const nombrePaciente = patientId ? (nombrePorPaciente.get(patientId) ?? '') : '';
      const nombreMed = mr.medicationCodeableConcept?.text ?? '';
      return normalizar(nombreMed).includes(textoBuscado) || normalizar(nombrePaciente).includes(textoBuscado);
    }
    return esDeHoy(mr.authoredOn);
  });

  return (
    <div className="space-y-3">
      <Input
        type="search"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder={t('farmaciaApp.cola.buscarPlaceholder')}
        aria-label={t('farmaciaApp.cola.buscarPlaceholder')}
      />
      {pendientes.length === 0 ? (
        <p className="text-sm text-hce-muted">{buscando ? t('farmaciaApp.cola.sinResultados') : t('farmaciaApp.cola.vacioHoy')}</p>
      ) : (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-hce-ink">
            {buscando ? t('farmaciaApp.cola.tituloBusqueda', { cantidad: pendientes.length }) : t('farmaciaApp.cola.titulo', { cantidad: pendientes.length })}
          </h2>
          <div className="divide-y divide-border rounded-lg border border-border">
            {pendientes.map((mr) => (
              <FilaReceta key={mr.id} receta={mr} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FilaReceta({ receta }: { receta: MedicationRequest }): JSX.Element {
  const { t } = useTranslation();
  const paciente = useResource<Patient>(receta.subject?.reference ? { reference: receta.subject.reference } : undefined);
  const nombrePaciente = paciente ? formatHumanName(paciente.name?.[0]) : (receta.subject?.display ?? '…');
  const cantidad = receta.dispenseRequest?.quantity;
  const medplumPatientId = receta.subject?.reference?.split('/')[1];

  // Vender ya no es un paso aparte (2026-09-11): clic acá lleva directo a
  // Órdenes con el paciente y esta receta preseleccionados — ver
  // OrdenesFarmacia.tsx, que resuelve `paciente` (id de Medplum) contra
  // listarPacientesHce() para encontrar el lead y traer sus recetas
  // pendientes, incluida esta.
  return (
    <Link
      to={medplumPatientId ? `/farmacia/facturacion?paciente=${medplumPatientId}&receta=${receta.id}` : '#'}
      className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm hover:bg-hce-paper"
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-hce-ink">{receta.medicationCodeableConcept?.text ?? t('farmaciaApp.cola.sinNombre')}</span>
        <span className="block text-xs text-hce-muted">
          {nombrePaciente}
          {cantidad ? ` · ${cantidad.value} ${cantidad.unit ?? ''}` : ''}
        </span>
      </span>
      <span className="shrink-0 text-xs text-hce-muted">{receta.authoredOn ? new Date(receta.authoredOn).toLocaleDateString('es-EC') : ''}</span>
    </Link>
  );
}
